import React from 'react';
import styled from 'styled-components';

import Typography from 'kingpin/atoms/Typography';

// 'answer' chips are the options that answer a clarifying question the agent
// just asked (rendered accented, so they read as choices to pick); 'suggestion'
// chips are neutral follow-up / starter prompts.
export type ChipVariant = 'answer' | 'suggestion';

const ChipList = styled.div<{ $variant: ChipVariant }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: ${({ $variant }) =>
    $variant === 'answer' ? 'flex-start' : 'center'};
  gap: 8px;
`;

const Chip = styled.button<{ $variant: ChipVariant }>`
  text-align: left;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'answer'
        ? 'var(--color-ai-accent)'
        : 'var(--color-border-secondary)'};
  background-color: ${({ $variant }) =>
    $variant === 'answer'
      ? 'var(--color-ai-accent-subtle)'
      : 'var(--color-bg-secondary)'};
  cursor: pointer;
  transition:
    background-color 0.12s ease,
    border-color 0.12s ease;

  &:hover:not(:disabled) {
    background-color: var(--color-ai-accent-subtle);
    border-color: var(--color-ai-accent);
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

// A wrapping row of pill-shaped chips, shared by the new-session starter
// suggestions, the per-answer follow-ups, and the clarifying-answer options.
const SuggestionChips = ({
  questions,
  onSelect,
  isDisabled,
  variant = 'suggestion',
}: {
  questions: string[];
  onSelect: (question: string) => void;
  isDisabled?: boolean;
  variant?: ChipVariant;
}) => (
  <ChipList $variant={variant}>
    {questions.map((question) => (
      <Chip
        key={question}
        $variant={variant}
        type="button"
        disabled={isDisabled}
        onClick={() => onSelect(question)}
      >
        <Typography.Body type="Body 12">{question}</Typography.Body>
      </Chip>
    ))}
  </ChipList>
);

export default SuggestionChips;
